import { useEffect } from 'react';
import { useActionData, useNavigation, useNavigate } from 'react-router';
import { useToast } from '../contexts/ToastContext';
import { useErrorHandler } from './useErrorHandler';

interface UseOnboardingFormOptions {
  /**
   * Route to navigate to on successful submission
   */
  nextRoute?: string;
  
  /**
   * Success message to show in toast
   */
  successMessage?: string;
  
  /**
   * Error message to show in toast (defaults to error from response)
   */
  errorMessage?: string;
  
  /**
   * Callback to execute on successful submission
   */
  onSuccess?: () => void;
  
  /**
   * Callback to execute on error
   */
  onError?: (error: string) => void;
}

/**
 * Centralized hook for onboarding form handling
 * Provides consistent loading states, toast notifications, and navigation
 * Works with both React Router form actions and programmatic submissions
 */
export function useOnboardingForm(options: UseOnboardingFormOptions = {}) {
  const navigation = useNavigation();
  const actionData = useActionData() as any;
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { handleError } = useErrorHandler();
  
  const {
    nextRoute,
    successMessage = 'Progress saved!',
    errorMessage,
    onSuccess,
    onError
  } = options;

  // Check if form is currently submitting (for React Router forms)
  const isSubmitting = navigation.state === 'submitting';

  // Handle form action results - FIXED: Only depend on actionData to prevent infinite loops
  useEffect(() => {
    if (!actionData) return;
    
    if (actionData.success) {
      // Show success toast
      showSuccess(successMessage);
      
      // Execute success callback
      onSuccess?.();
      
      // Navigate to next route if specified (longer delay to ensure render completes)
      if (nextRoute) {
        setTimeout(() => navigate(nextRoute), 100);
      }
    } else if (actionData.error) {
      // Show error toast
      const errorMsg = errorMessage || actionData.error;
      showError('Error', errorMsg);
      
      // Execute error callback
      onError?.(errorMsg);
      
      // Log error for debugging
      handleError(new Error(errorMsg));
    }
  }, [actionData]); // CRITICAL: Only depend on actionData!

  /**
   * Execute an async operation with centralized loading/error/success handling
   * Use this for programmatic submissions (like the resume upload)
   */
  const executeAsync = async <T,>(
    operation: () => Promise<T>,
    options?: {
      successMessage?: string;
      errorMessage?: string;
      onSuccess?: (result: T) => void;
      onError?: (error: string) => void;
    }
  ): Promise<T | undefined> => {
    try {
      const result = await operation();
      
      // Show success toast
      const successMsg = options?.successMessage || successMessage;
      showSuccess(successMsg);
      
      // Execute success callback
      options?.onSuccess?.(result);
      onSuccess?.();
      
      // Navigate to next route if specified
      if (nextRoute) {
        navigate(nextRoute);
      }
      
      return result;
    } catch (error: any) {
      // Show error toast
      const errorMsg = options?.errorMessage || errorMessage || error.message || 'An error occurred';
      showError('Error', errorMsg);
      
      // Execute error callback
      options?.onError?.(errorMsg);
      onError?.(errorMsg);
      
      // Log error
      handleError(error);
      
      return undefined;
    }
  };

  return {
    /**
     * Whether a form submission is currently in progress
     */
    isSubmitting,
    
    /**
     * Execute an async operation with centralized handling
     * Perfect for programmatic submissions like file uploads
     */
    executeAsync,
    
    /**
     * Manual navigation helper
     */
    navigate: (route: string) => navigate(route),
    
    /**
     * Show success toast manually
     */
    showSuccess: (message: string) => showSuccess(message),
    
    /**
     * Show error toast manually
     */
    showError: (message: string) => showError('Error', message),
  };
}

/**
 * Helper function for standardized onboarding action handling
 * Use this in React Router form actions for consistent error handling
 */
export async function handleOnboardingAction(
  operation: () => Promise<void>,
  _stepCompleted?: string
) {
  try {
    // Execute the main operation (e.g., save profile data)
    await operation();
    
    // Note: Onboarding progress is now tracked through Better Auth user profile
    // No need for separate onboarding advancement API calls

    return { success: true };
  } catch (error: any) {
    return { error: error.message || 'Failed to save progress' };
  }
}